export const models = {
  "bedrock_converse/us.meta.llama4-maverick-17b-instruct-v1:0":
    "Llama 4 Maverick 17B Instruct",
  "bedrock_converse/us.meta.llama3-3-70b-instruct-v1:0": "Llama 3 70B Instruct",
  "fireworks/accounts/fireworks/models/kimi-k2p6": "Kimi 2.6",
  "fireworks/accounts/fireworks/models/kimi-k2p5": "Kimi 2.5",
  "fireworks/accounts/fireworks/models/gpt-oss-120b": "GPT-OSS 120B",
  "fireworks/accounts/fireworks/models/deepseek-v4-flash": "DeepSeek V4 Flash",
  "openai/gpt-4o-mini": "GPT-4o Mini",
  "openai/gpt-4o": "GPT-4o",
  "openai/gpt-4.1-mini": "GPT-4.1 Mini",
  "openai/gpt-5.6-luna": "GPT-5.6 Luna",
  "openai/gpt-5-mini": "GPT-5 Mini",
  "openai/gpt-4.1": "GPT-4.1",
  "openai/o4-mini": "o4 Mini",
  "openai/o3-mini": "o3 Mini",
  "openai/o1": "o1",
  "openai/gpt-4.1-nano": "GPT-4.1 Nano",
};

export const modelPrices: Record<
  keyof typeof models,
  { label: string; input: string; cached: string; output: string } | undefined
> = {
  "bedrock_converse/us.meta.llama4-maverick-17b-instruct-v1:0": {
    label: "Llama 4 Maverick 17B Instruct",
    input: "$0.24",
    cached: "N/A",
    output: "$0.97",
  },
  "bedrock_converse/us.meta.llama3-3-70b-instruct-v1:0": {
    label: "Llama 3 70B Instruct",
    input: "$0.36",
    cached: "N/A",
    output: "$0.66",
  },
  "fireworks/accounts/fireworks/models/kimi-k2p6": {
    label: "Kimi 2.6",
    input: "$0.95",
    cached: "$0.16",
    output: "$4.00",
  },
  "fireworks/accounts/fireworks/models/kimi-k2p5": {
    label: "Kimi 2.5",
    input: "$0.60",
    cached: "$0.10",
    output: "$3.00",
  },
  "fireworks/accounts/fireworks/models/gpt-oss-120b": {
    label: "GPT-OSS 120B",
    input: "$0.15",
    cached: "$0.01",
    output: "$0.60",
  },
  "fireworks/accounts/fireworks/models/deepseek-v4-flash": {
    label: "DeepSeek V4 Flash",
    input: "$0.14",
    cached: "$0.03",
    output: "$0.28",
  },
  "openai/gpt-4o-mini": {
    label: "GPT-4o Mini",
    input: "$0.15",
    cached: "$0.075",
    output: "$0.60",
  },
  "openai/gpt-4o": {
    label: "GPT-4o",
    input: "$2.50",
    cached: "$1.25",
    output: "$10.00",
  },
  "openai/gpt-4.1-mini": {
    label: "GPT-4.1 Mini",
    input: "$0.40",
    cached: "$0.10",
    output: "$1.60",
  },
  "openai/gpt-4.1": {
    label: "GPT-4.1",
    input: "$2.00",
    cached: "$0.50",
    output: "$8.00",
  },
  "openai/gpt-5.6-luna": {
    label: "GPT-5.6 Luna",
    input: "$0.20",
    cached: "$0.02",
    output: "$1.20",
  },
  "openai/gpt-5-mini": {
    label: "GPT-5 Mini",
    input: "$0.25",
    cached: "$0.025",
    output: "$2.00",
  },
  "openai/o4-mini": {
    label: "o4 Mini",
    input: "$1.10",
    cached: "$0.275",
    output: "$4.40",
  },
  "openai/o3-mini": {
    label: "o3 Mini",
    input: "$1.10",
    cached: "$0.55",
    output: "$4.40",
  },
  "openai/o1": {
    label: "o1",
    input: "$15.00",
    cached: "$7.50",
    output: "$60.00",
  },
  "openai/gpt-4.1-nano": {
    label: "GPT-4.1 Nano",
    input: "$0.10",
    cached: "$0.025",
    output: "$0.40",
  },
};

// ---------------------------------------------------------------------------
// Reasoning effort (staff-only, mirrors the backend)
// ---------------------------------------------------------------------------

// Keep in sync with `ReasoningEffort` / `DEFAULT_REASONING_EFFORT` in
// app/ai_search/configuration.py. The backend validates the value on the chat
// payload, so an unknown string here is a 422 rather than a silent fallback --
// which is why the value read back from storage is checked before it is sent.
// 'minimal' and 'none' are deliberately absent: gpt-5.6 rejects the first, and
// the second is what 'off' sends on the caller's behalf.
export const reasoningEfforts = {
  off: "Off",
  low: "Low",
  medium: "Medium",
  high: "High",
} as const;

export type ReasoningEffort = keyof typeof reasoningEfforts;

export const defaultReasoningEffort: ReasoningEffort = "low";

// Models that actually read the setting -- mirrors RESPONSES_API_OPENAI_MODELS
// in app/ai_search/utils/chat_util.py. Every other model ignores reasoning
// effort entirely (gpt-5-mini included: it reasons, but is deliberately left on
// chat completions), so the picker is hidden for them instead of offering a
// control that does nothing.
const reasoningModelPrefixes = ["openai/gpt-5.6"];

// Mirrors DEFAULT_MODEL in app/ai_search/configuration.py -- what the backend
// falls back to when the request carries no model override.
export const defaultModel = "openai/gpt-4.1-mini";
// export const defaultModel = "openai/gpt-4o";

export const modelStorageKey = "har_ai_search_model";
export const reasoningEffortStorageKey = "har_ai_search_reasoning_effort";

export const supportsReasoningEffort = (model?: string | null): boolean =>
  !!model &&
  reasoningModelPrefixes.some((prefix) =>
    model.toLowerCase().startsWith(prefix),
  );

// `hasOwnProperty` rather than `in`: `in` also walks the prototype chain, so
// "toString" and "constructor" would pass as efforts and reach the backend as a
// 422 -- exactly what the guard exists to prevent.
export const isReasoningEffort = (value: unknown): value is ReasoningEffort =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(reasoningEfforts, value);

// localStorage throws in private-mode Safari and when site data is blocked, and
// a header dropdown is not worth taking the app down for, so both accessors
// degrade to the default rather than propagating.
export const isKnownModel = (value: unknown): value is keyof typeof models =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(models, value);

export const getStoredModel = (): string | null => {
  try {
    const stored = localStorage.getItem(modelStorageKey);
    // A model the picker cannot offer is not one the request should carry: a
    // stale or hand-edited id renders the controlled select blank and reaches
    // the backend as an unknown provider/model. Null instead, so the header
    // falls back to `defaultModel` and the request to the backend default.
    return isKnownModel(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const getStoredReasoningEffort = (): ReasoningEffort => {
  try {
    const stored = localStorage.getItem(reasoningEffortStorageKey);
    return isReasoningEffort(stored) ? stored : defaultReasoningEffort;
  } catch {
    return defaultReasoningEffort;
  }
};

// Both writers report whether the value actually landed. Storage is where the
// chat request reads the override back from, so a caller that keeps UI state in
// step has to know a blocked write happened rather than showing a choice that
// will never be sent.
export const storeModel = (model: string): boolean => {
  try {
    localStorage.setItem(modelStorageKey, model);
    return true;
  } catch {
    console.warn("Could not save the model to localStorage");
    return false;
  }
};

export const storeReasoningEffort = (effort: ReasoningEffort): boolean => {
  try {
    localStorage.setItem(reasoningEffortStorageKey, effort);
    return true;
  } catch {
    console.warn("Could not save the reasoning effort to localStorage");
    return false;
  }
};

/**
 * The `reasoning_effort` field to merge into a chat request payload.
 *
 * Empty for models that ignore the setting, so the request stays byte-identical
 * to what it was before the picker existed rather than carrying a field the
 * backend would drop anyway.
 */
export const reasoningEffortPayload = (
  model?: string | null,
): { reasoning_effort?: ReasoningEffort } =>
  supportsReasoningEffort(model)
    ? { reasoning_effort: getStoredReasoningEffort() }
    : {};
